import { useEffect, useState } from 'react'

// SPA fallback for the OAuth popup completion page.
//
// The provider auth-callback edge functions 302-redirect to
// `/oauth-complete.html` on this origin. Normally the static public/oauth-complete.html
// (+ oauth-complete.js) handles it, and the PWA service worker's
// navigateFallbackDenylist lets that static page through. But if a service
// worker serves the SPA app-shell for that navigation instead — most importantly
// a STALE pre-denylist worker that hasn't updated yet, or any future SW that
// intercepts the nav — React Router previously had no matching route, so the
// popup rendered BLANK (no postMessage to the opener, no auto-close).
//
// This route runs the SAME completion logic as oauth-complete.js, so the popup
// finishes correctly whether the static page OR the app-shell is served. Because
// it ships inside the app bundle, every build from here on completes OAuth even
// when a worker serves the shell — making the blank-popup failure non-recurring.
export function OAuthComplete() {
  const [view, setView] = useState<{ heading: string; message: string; showLink: boolean }>({
    heading: '',
    message: 'Finishing up…',
    showLink: false,
  })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('status') === 'success') {
      try {
        // URLSearchParams already percent-decodes; the payload is the JSON the
        // callback put in `data` (encodeURIComponent(JSON.stringify(message))).
        const msg = JSON.parse(params.get('data') || '{}')
        if (window.opener) window.opener.postMessage(msg, window.location.origin)
      } catch {
        // Malformed payload: still confirm + close; the opener simply won't get an update.
      }
      // I approve bandaid: this is a static popup that derives its view once from
      // window.location on mount — a legitimate sync setState in a mount-only
      // effect, not a render-loop hazard. (Same disposition as Analytics/Commissions.)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setView({ heading: 'Connected', message: 'You can close this window and return to Rize OS.', showLink: false })
      const t = setTimeout(() => {
        try { window.close() } catch { /* some browsers block programmatic close */ }
      }, 1500)
      return () => clearTimeout(t)
    }
    setView({
      heading: params.get('title') || 'Connection Failed',
      message: params.get('message') || 'Something went wrong. Please try connecting again.',
      showLink: true,
    })
  }, [])

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#09090B',
        color: '#FAFAFA',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        textAlign: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 400 }}>
        {view.heading && <h2 style={{ margin: '0 0 8px', fontSize: '1.25rem' }}>{view.heading}</h2>}
        <p style={{ margin: '8px 0', lineHeight: 1.5, opacity: 0.9 }}>{view.message}</p>
        {view.showLink && (
          <p>
            <a href="/" style={{ color: '#E11D48', textDecoration: 'none' }}>Return to Rize OS</a>
          </p>
        )}
      </div>
    </div>
  )
}
